import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const SITE_NAME = 'South East State of Somalia — State House';
const DEFAULT_DESC =
  'Official portal of the South East State of Somalia — news, events, ministries, investment opportunities and government services.';
const DEFAULT_KEYWORDS =
  'South East State Somalia, State House Somalia, Somalia government, Hooseqaboobe, Marka Cadeey, Somalia investment, Somali ministries, Somali Federal State';

const getOrigin = () => {
  const envOrigin = process.env.REACT_APP_PUBLIC_URL;
  if (envOrigin) return envOrigin.replace(/\/$/, '');
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/$/, '');
  }
  return '';
};

const absolute = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  const origin = getOrigin();
  return `${origin}${url.startsWith('/') ? '' : '/'}${url}`;
};

const SEO = ({
  title,
  description = DEFAULT_DESC,
  keywords,
  image,
  type = 'website',
  noindex = false,
  jsonLd,
  lang = 'en',
}) => {
  const location = useLocation();
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const url = `${getOrigin()}${location.pathname}`;
  const ogImage = absolute(image) || `${getOrigin()}/og-default.svg`;

  return (
    <Helmet prioritizeSeoTags>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords || DEFAULT_KEYWORDS} />
      {noindex ? (
        <meta name="robots" content="noindex,nofollow" />
      ) : (
        <meta name="robots" content="index,follow" />
      )}
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content={lang === 'so' ? 'so_SO' : 'en_US'} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
};

export default SEO;
